import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useDataApiClient } from "../../hooks/useApiClient";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Skeleton,
  SkeletonText,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { EditIcon } from "@chakra-ui/icons";
import { useDataApiRequest } from "../../hooks/useApiRequest";
import { CopyText } from "../../components/Utilities/CopyText";
import { TwilioConfigurationForm } from "../../forms/TwilioConfigurationForm";
import { CherryHubDataApi } from "../../api/dataApiModels";

interface TwilioConfigurationProps {
  create?: boolean;
}

export const TwilioConfiguration = (props: TwilioConfigurationProps) => {
  const [searchParams] = useSearchParams();
  const { twilioConfigurationId } = useParams();
  const navigate = useNavigate();
  const [twilioConfiguration, setTwilioConfiguration] =
    useState<CherryHubDataApi.TwilioConfiguration | null>(null);
  const [terminalUrls, setTerminalUrls] = useState<{ [key: string]: string }>(
    {}
  );
  const apiClient = useDataApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  const {
    data: twilioConfigurationDoc,
    isLoading,
    error,
  } = useDataApiRequest(
    (apiClient) => {
      if (!twilioConfigurationId) {
        return null;
      }
      return apiClient.getTwilioConfiguration(twilioConfigurationId);
    },
    [twilioConfigurationId]
  );

  useEffect(() => {
    if (twilioConfigurationDoc != null && props.create) {
      setEditMode(false);
      navigate(`/admin/twilio-configuration/${twilioConfigurationId}`);
    }
    setTwilioConfiguration(twilioConfigurationDoc);
  }, [twilioConfigurationDoc]);

  const onSuccess = useCallback(
    (twilioConfiguration: CherryHubDataApi.TwilioConfiguration) => {
      setTwilioConfiguration(twilioConfiguration);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Twilio Configuration created.");
        navigate(`/admin/twilio-configuration/${twilioConfigurationId}`);
      } else {
        showSuccessToast("Twilio Configuration updated.");
      }
    },
    [showSuccessToast, twilioConfiguration]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Twilio Configuration update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Twilio Configuration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          {(isLoading || !twilioConfiguration) && !props.create && !error && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="space-between">
                <Skeleton w="320px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
              <SkeletonText w="100%" mt="4" noOfLines={4} spacing="4" />
            </VStack>
          )}

          {!isLoading && error && !editMode && (
            <VStack w="100%" p={4} spacing={2}>
              <Text fontStyle="italic" fontSize="sm">
                Twilio Configuration has not been set.
              </Text>
              <Button
                size="sm"
                leftIcon={<PlusIcon />}
                onClick={() => {
                  navigate(`/admin/twilio-configuration/new`);
                  setEditMode(true);
                }}
                colorScheme="cherryButton"
              >
                Create Twilio Configuration
              </Button>
            </VStack>
          )}

          {!isLoading &&
            editMode &&
            (!props.create ? twilioConfiguration : true) && (
              <TwilioConfigurationForm
                apiClient={apiClient}
                twilioConfiguration={twilioConfiguration}
                onCancel={() => {
                  if (props.create) navigate(`/data-api/sysadmin/environment`);
                  else setEditMode(false);
                }}
                onSuccess={onSuccess}
                onFailure={onFailure}
              />
            )}

          {!isLoading && !editMode && twilioConfiguration && (
            <>
              <HStack w="100%">
                <Box flexGrow={1} pb={4}>
                  <CopyText
                    text={twilioConfiguration?.Meta?.Id!}
                    hover
                    adjustMargin
                  >
                    <Text fontSize="md" color="cherryUi.600">
                      {twilioConfiguration?.Meta?.Id}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{twilioConfiguration?.ServiceName}</b>
                  </Text>
                </Box>

                <Box>
                  <Button
                    leftIcon={<EditIcon />}
                    colorScheme="cherryButton"
                    borderRight="1px"
                    onClick={() => setEditMode(true)}
                  >
                    Edit
                  </Button>
                </Box>
              </HStack>

              <ViewControl
                label="Service Name"
                value={twilioConfiguration?.ServiceName ?? "-"}
              />

              <ViewControl
                label="Parent Account Id"
                value={twilioConfiguration?.ParentAccountId ?? "-"}
              />

              <ViewControl
                label="Default Account Id"
                value={twilioConfiguration?.DefaultAccount?.AccountId ?? "-"}
              />

              <ViewControl
                label="Default SMS Sender Id"
                value={twilioConfiguration?.DefaultAccount?.SmsSenderId ?? "-"}
              />

              <ViewControl label="Sub Accounts">
                <Table size="sm" flex={3}>
                  <Thead>
                    <Tr>
                      <Th>Account Id</Th>
                      <Th>SMS Sender Id</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {twilioConfiguration.SubAccounts?.map((subAccount, idx) => (
                      <Tr key={idx}>
                        <Td>{subAccount.AccountId}</Td>
                        <Td>{subAccount.SmsSenderId}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </ViewControl>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
